import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  TextField,
  Alert,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
  grayPrimaryFontColor,
} from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import MessageNode from "../Whatsapp/MessageNode";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_SEND_REPORT_ENDPOINT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DetailTile = ({ label, value }) => (
  <Box sx={{ backgroundColor: dashboardCardBgColor, borderRadius: "8px", px: 1.5, py: 1.25 }}>
    <Typography
      sx={{
        ...typography.bodySmall,
        color: grayFontColor,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
      {value || "—"}
    </Typography>
  </Box>
);

// Map Meta WhatsApp button types → MessageNode button types
const BUTTON_TYPE_MAP = {
  URL:          "visit-website",
  PHONE_NUMBER: "call-phone-number",
  QUICK_REPLY:  "custom",
  OPT_OUT:      "marketing-opt-out",
};

// Map Meta header format → MessageNode headerType
const HEADER_TYPE_MAP = {
  TEXT:     "text",
  IMAGE:    "image",
  VIDEO:    "video",
  DOCUMENT: "document",
  LOCATION: "location",
};

const parseJson = (val) => {
  if (!val) return null;
  if (typeof val !== "string") return val;
  try { return JSON.parse(val); } catch { return null; }
};

// Build MessageNode props from either Penguin's pre-shaped template_preview_json
// or the raw Meta-format template_json (with `components` array).
const buildPreviewProps = (template) => {
  if (!template) return null;

  const preview = parseJson(template.template_preview_json);
  if (preview && typeof preview === "object") {
    return {
      templateName:       template.template_name || "",
      marketingOption:    preview.marketingOption || null,
      language:           preview.language || "english",
      headerType:         preview.headerType || "none",
      headerText:         preview.headerText || "",
      headerFile:         preview.headerFile || null,
      body:               preview.body || "",
      footer:             preview.footer || "",
      allButtons:         preview.allButtons || [],
      sampleBodyFields:   preview.sampleBodyFields || [],
      sampleHeaderFields: preview.sampleHeaderFields || [],
      location:           preview.location || null,
      carouselCards:      preview.carouselCards,
      carouselType:       preview.carouselType,
      carouselMainBody:   preview.carouselMainBody,
    };
  }

  const json = parseJson(template.template_json);
  if (!json) return null;

  const components = Array.isArray(json.components) ? json.components : [];
  const header     = components.find((c) => c.type === "HEADER");
  const body       = components.find((c) => c.type === "BODY");
  const footer     = components.find((c) => c.type === "FOOTER");
  const buttons    = components.find((c) => c.type === "BUTTONS");

  return {
    templateName: template.template_name || json.name || "",
    language:     json.language || "english",
    headerType:   HEADER_TYPE_MAP[header?.format] || "none",
    headerText:   header?.text || "",
    headerFile:   header?.example?.header_handle?.[0] || null,
    body:         body?.text || "",
    footer:       footer?.text || "",
    allButtons: (buttons?.buttons || []).map((b) => ({
      type: BUTTON_TYPE_MAP[b.type] || "custom",
      data: { buttonText: b.text || "" },
    })),
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const ReportModal = ({ open, row, template, onClose, onSend }) => {
  const { getValidToken } = useAuth();

  const [note,     setNote]     = useState("");
  const [file,     setFile]     = useState(null);
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (row) {
      setNote(row.note || "");
      setFile(null);
      setSending(false);
      setSent(false);
      setApiError(null);
    }
  }, [row?.id]);

  if (!row) return null;

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      setApiError(null);
    }
  };

  const handleSend = async () => {
    if (!file) {
      setApiError("Please upload a PDF file before sending.");
      return;
    }

    setSending(true);
    setApiError(null);

    try {
      const token  = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formData = new FormData();
      formData.append("file",              file);
      formData.append("client_id",         row.client_id ?? row.client);
      formData.append("report_request_id", row.id);

      const res = await fetch(MFD_SEND_REPORT_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send report.");
      }

      setSent(true);
      setTimeout(() => onSend({ ...row, note }), 1800);
    } catch (err) {
      setApiError(err.message);
      setSending(false);
    }
  };

  const previewProps = buildPreviewProps(template);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: ColorsConstants.primaryBackgroundColor,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryWhite }}>
            Report Request
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: "rgba(255,255,255,0.5)", mt: 0.25 }}>
            View details, upload file &amp; send via WhatsApp
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={sending}
          sx={{
            color: ColorsConstants.primaryWhite,
            backgroundColor: "rgba(255,255,255,0.12)",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>

        {/* Detail tiles */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6}><DetailTile label="Client name"  value={row.client_name} /></Grid>
          <Grid item xs={6}><DetailTile label="PAN"          value={row.pan}         /></Grid>
          <Grid item xs={6}><DetailTile label="Report type"  value={row.type}        /></Grid>
          <Grid item xs={6}><DetailTile label="Period"       value={row.period}      /></Grid>
          <Grid item xs={6}>
            <DetailTile
              label="Requested at"
              value={row.created_at ? formatWithTzLabel(row.created_at) : "—"}
            />
          </Grid>
          <Grid item xs={6}><DetailTile label="Status" value={row.status} /></Grid>
        </Grid>

        {/* Editable note */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              ...typography.bodySmall,
              color: grayFontColor,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: 0.75,
              fontWeight: 600,
            }}
          >
            Note (editable)
          </Typography>
          <TextField
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add or update note for this request…"
            disabled={sending || sent}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                ...typography.bodyLarge,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: borderColors.cardBorderPrimaryGrayColor },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryPurple100 },
              },
              "& .MuiInputBase-input": { ...typography.bodyLarge, color: grayPrimaryFontColor },
            }}
          />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* PDF file upload */}
        <Box
          component="label"
          htmlFor="report-file-input"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: `1.5px dashed ${file ? ColorsConstants.primaryPurple100 : borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: "10px",
            backgroundColor: dashboardCardBgColor,
            py: 2.5,
            cursor: sending || sent ? "default" : "pointer",
            mb: 2.5,
            transition: "border-color 0.2s",
            "&:hover": { borderColor: sending || sent ? undefined : ColorsConstants.primaryPurple100 },
          }}
        >
          <UploadFileOutlinedIcon sx={{ fontSize: 32, color: ColorsConstants.primaryPurple100, mb: 1 }} />
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100, mb: 0.25 }}>
            {file ? file.name : "Upload report PDF"}
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            {file ? `${(file.size / 1024).toFixed(1)} KB — click to change` : "PDF only"}
          </Typography>
          <input
            id="report-file-input"
            type="file"
            accept="application/pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={sending || sent}
          />
        </Box>

        {/* WhatsApp template preview */}
        {previewProps && (
          <Box
            sx={{
              backgroundColor: "#e8f5e9",
              border: "1px solid #a5d6a7",
              borderRadius: "10px",
              px: 2,
              py: 1.5,
              mb: (apiError || sent) ? 2 : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
              <WhatsAppIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
              <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: "#2e7d32", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                WhatsApp template
                {template?.template_name ? ` — ${template.template_name}` : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <MessageNode
                {...previewProps}
                mode="view"
                showSampleValues
              />
            </Box>
          </Box>
        )}

        {/* Error */}
        {apiError && (
          <Alert
            icon={<ErrorOutlineIcon />}
            severity="error"
            sx={{ borderRadius: "8px", ...typography.bodySmall }}
          >
            {apiError}
          </Alert>
        )}

        {/* Success */}
        {sent && (
          <Alert
            icon={<CheckCircleOutlineIcon />}
            severity="success"
            sx={{ borderRadius: "8px", ...typography.bodySmall }}
          >
            Report sent to <strong>{row.client_name}</strong> via WhatsApp.
          </Alert>
        )}

      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 0,
          gap: 1,
          borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          mt: 1,
        }}
      >
        <ButtonTwo onClick={onClose} disabled={sending || sent}>Cancel</ButtonTwo>
        <ButtonOne
          onClick={handleSend}
          disabled={sending || sent}
          startIcon={
            sending
              ? <CircularProgress size={14} sx={{ color: "inherit" }} />
              : <WhatsAppIcon sx={{ fontSize: 16 }} />
          }
          sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
        >
          {sent ? "Sent" : sending ? "Sending…" : "Send via WhatsApp"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default ReportModal;
